import { Component, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, Subject, Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { LoginService } from '../../../../pages/login/login.service';
import { AppService, UserService } from '../../../../shared/service/user.service';
import { ConfirmService } from '../../../../shared/components/confirm/confirm.service';
import { AppSubject } from '../../../../shared/app-subject';
import { InvoiceService } from '../../../customer/invoice/invoice.service';
import { AgreementSigningHandlerService } from './AgreementSigningHandler.service';

interface RunValue {
  idValue?: string;
  invoiceId?: string;
  idValues?: any;
  actionType: string;
  issueStatusId?: string;
  taxCode?: string;
  requireLoginKey?: boolean;
  pinCode?: string;
  signingData?: any;
  referenceCode?: string;
  agreementContentId?: string;
  signedByRole?: 'SELLER' | 'BUYER';
  signedByPartyId?: string;
}

interface SigningAttempt {
  attemptId: string;
  userId: string;
  signingType: string;
  idValue: string;
  invoiceId?: string;
  createdAt: number;
}

@Injectable({
  providedIn: `root`
})
export class SigningService {

  readonly errorSubject: Subject<any>;
  readonly displaySubject: Subject<any>;
  readonly batchSigningViewSubject: Subject<any>;
  addCTSSubscription?: Subscription;
  private batchResumePrompted = false;

  private runResolve?: (value: void | PromiseLike<void>) => void;
  private runReject?: (reason?: any) => void;

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    private confirmService: ConfirmService,
    private loginService: LoginService,
    private invoiceService: InvoiceService,
    private agreementSigningHandler: AgreementSigningHandlerService
  ) {
    this.http = new HttpClient(this.backend);
    this.errorSubject = new Subject<any>();
    this.displaySubject = new Subject<any>();
    this.batchSigningViewSubject = new Subject<any>();
  }

  run(runValue: RunValue): Promise<void> {
    AppSubject.setReloadData('');

    let signingType = UserService.get('SIGNING');
    let signingTypes = UserService.get('SIGNING_TYPES');

    if (!signingTypes) {
      const user = UserService.getUser();
      if (user?.signingTypes?.length > 0) {
        signingTypes = JSON.stringify(user.signingTypes);
        UserService.set(UserService.KEY.SIGNING_TYPES, signingTypes);
      } else {
        signingTypes = 'NULL';
        UserService.set(UserService.KEY.SIGNING_TYPES, 'NULL');
      }
    }
    const recordSigning = this.isRecordSigning(runValue);

    if (!recordSigning && signingTypes === 'NULL' && !signingType) {
      signingType = 'UsbTokenSigning';
    }

    if (!recordSigning && signingTypes !== 'NULL' && !signingType && signingType !== 'newCert') {
      this.displaySubject.next('form1');
    }

    if (!this.runResolve) {
      return new Promise((resolve, reject) => {
        this.runResolve = (v) => {
          resolve(v);
          this.cleanupResolver();
        };
        this.runReject = (e) => {
          reject(e);
          this.cleanupResolver();
        };
        this.executeRun(runValue, signingType, signingTypes);
      });
    } else {
      this.executeRun(runValue, signingType, signingTypes);
      return Promise.resolve();
    }
  }

  private cleanupResolver(): void {
    this.runResolve = undefined;
    this.runReject = undefined;
  }

  cancel(): void {
    if (this.runReject) {
      this.runReject('Signing cancelled by user');
    }
    this.cleanupResolver();
    AppSubject.setReloadData('');
  }

  private executeRun(runValue: RunValue, signingType: string, signingTypes: string): void {
    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    if (this.isRecordSigning(runValue) && !signingType) {
      this.loadRecordSigningMethods(runValue);
    } else if (!signingType && signingTypes !== 'NULL') {
      this.displaySubject.next('form1');
    } else if (this.isPdfRecordSigning(runValue) && signingType === 'UsbTokenSigning' && runValue.referenceCode) {
      this.handleAgreementPdfUsbSigning(runValue);
    } else if (signingType === 'UsbTokenSigning' || signingType === 'newCert' || signingTypes === 'NULL') {
      const useSigningAttempt = this.isUsbTokenSigning(signingType, signingTypes);
      if (runValue.idValues && runValue.idValues.length > 0) {
        this.handleUsbBatchSigning(runValue, useSigningAttempt);
      } else {
        this.handleUsbSigning(runValue, useSigningAttempt);
      }
    } else {
      this.handleRemoteSigningFlow(signingType);
    }
  }

  private isRecordSigning(runValue?: RunValue): boolean {
    return runValue?.actionType === 'agreementInvoiceAdj';
  }

  private isPdfRecordSigning(runValue?: RunValue): boolean {
    return this.isRecordSigning(runValue) && !!(runValue?.referenceCode || runValue?.agreementContentId);
  }

  isCurrentPdfRecordSigning(): boolean {
    return this.isPdfRecordSigning(this.getCurrentRunValue());
  }

  private async loadRecordSigningMethods(runValue: RunValue): Promise<void> {
    const params = new URLSearchParams();
    if (this.isPdfRecordSigning(runValue)) {
      params.set('signingType', 'agreementContentPdf');
      if (runValue.referenceCode) {
        params.set('referenceCode', runValue.referenceCode);
      }
      if (runValue.agreementContentId) {
        params.set('agreementContentId', runValue.agreementContentId);
      }
    } else {
      params.set('signingType', runValue.actionType);
    }
    if (!this.isPdfRecordSigning(runValue) && runValue.idValue) {
      params.set('idValue', runValue.idValue);
    }

    try {
      const url = `${window.location.origin}/api/rest/s1/iam-common/record-signing-methods?${params.toString()}`;
      const result: any = await this.http.get(url, this.getHttpOptions()).toPromise();
      const methods = result?.listData || [];
      if (!methods.length) {
        this.showErrorDialog({ errorMessage: 'Không có hình thức ký biên bản khả dụng' });
        this.runReject?.(result);
        return;
      }
      UserService.set('RECORD_SIGNING_TYPES', JSON.stringify(methods));
      this.displaySubject.next('form1');
    } catch (error) {
      this.showErrorDialog(error);
      this.runReject?.(error);
    }
  }

  private isUsbTokenSigning(signingType: string, signingTypes: string): boolean {
    return signingType === 'UsbTokenSigning' || (!signingType && signingTypes === 'NULL');
  }

  private handleUsbBatchSigning(runValue: RunValue, useSigningAttempt: boolean): void {
    const isInvoiceBatch = runValue.actionType === 'invoiceBatchSigning';
    const isPosBatch = runValue.actionType === 'posBatchSigning';

    if (isInvoiceBatch) {
      this.invoiceService.putToWorkEffort(runValue.idValues).subscribe({
        next: ({ workEffortId }: any) => {
          this.handleUsbSigning({ idValue: workEffortId, actionType: 'invoiceBatchSigning' }, useSigningAttempt);
        },
        error: (err: any) => this.runReject?.(err)
      });
    } else if (isPosBatch) {
      this.invoiceService.POSSigning(runValue.idValues).subscribe({
        next: (res: any) => {
          this.handleUsbSigning({ idValue: res.workEffortId, actionType: 'posBatchSigning', signingData: res }, useSigningAttempt);
        },
        error: (err: any) => this.runReject?.(err)
      });
    } else {
      this.handleUsbSigning(runValue, useSigningAttempt);
    }
  }

  private async handleUsbSigning(runValue: RunValue, useSigningAttempt: boolean): Promise<void> {
    try {
      // Giữ nguyên cổng local gốc 13369
      const testRes: any = await this.http.get('http://localhost:13369/test').pipe(timeout(1000)).toPromise();

      if (testRes?.receiveDataFromBrowser === "Y") {
        await this.runModernUsbFlow(runValue, useSigningAttempt);
      } else {
        await this.runLegacyUsbFlow(runValue, useSigningAttempt);
      }
    } catch (error: any) {
      if (error.status === 200) {
        await this.runLegacyUsbFlow(runValue, useSigningAttempt);
      } else if (useSigningAttempt && this.isSigningAttemptGuardError(error)) {
        this.showErrorDialog(error);
        this.runReject?.(error);
      } else {
        await this.handleUsbError(error, runValue, useSigningAttempt);
      }
    } finally {
      UserService.set(UserService.KEY.SIGNING, '');
    }
  }

  private async runModernUsbFlow(runValue: RunValue, useSigningAttempt: boolean): Promise<void> {
    const commonUrl = `${window.location.origin}/api/rest/s1/iam-common`;

    try {
      const attempt = useSigningAttempt ? this.prepareSigningAttempt(runValue) : undefined;
      let signingResult = runValue.signingData;

      if (!signingResult) {
        const data: any = {
          signingType: runValue.actionType,
          idValue: runValue.idValue,
          taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
          responseContentType: "application/json"
        };
        if (attempt?.attemptId) {
          data.signingAttemptId = attempt.attemptId;
        }
        signingResult = await this.http.put(`${commonUrl}/signing`, data, this.getHttpOptions()).toPromise();
      }

      this.displaySubject.next('form3');
      // Giữ nguyên cổng local gốc 13369
      const response: any = await this.http.post('http://localhost:13369/signing', signingResult).toPromise();

      if (response?.success) {
        const completePayload = Object.assign({}, response);
        if (attempt?.attemptId) {
          completePayload.signingAttemptId = response?.signingAttemptId || signingResult?.signingAttemptId || attempt.attemptId;
        } else {
          delete completePayload.signingAttemptId;
        }
        const completeResult: any = await this.http.post(`${commonUrl}/complete-signing`, completePayload, this.getHttpOptions()).toPromise();
        this.processFinalResult(completeResult, useSigningAttempt);
      } else if (response?.errorMessage) {
        this.showErrorDialog(response);
        await this.completeSigning(runValue, "N", useSigningAttempt);
        this.runReject?.(response);
      } else {
        const error = { errorMessage: this.getErrorMessage(response) };
        this.showErrorDialog(error);
        await this.completeSigning(runValue, "N", useSigningAttempt);
        this.runReject?.(error);
      }
    } catch (error: any) {
      this.handleSigningError(error, runValue, true, useSigningAttempt);
      this.handleFinalError();
      this.runReject?.(error);
      throw error;
    }
  }

  private async runLegacyUsbFlow(runValue: RunValue, useSigningAttempt: boolean): Promise<void> {
    let loginKey = '';
    if (runValue.requireLoginKey !== false) {
      const apiKey: any = await this.loginService.getApiKey().toPromise();
      loginKey = apiKey.token;
    }

    const data: any = {
      baseUrl: window.location.origin + '/api',
      loginKey,
      csrfToken: AppService.get('MOQUI_SESSION_TOKEN'),
      actionType: runValue.actionType,
      idValue: runValue.idValue,
      taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
    };
    if (useSigningAttempt) {
      const attempt = this.prepareSigningAttempt(runValue);
      if (attempt?.attemptId) {
        data.signingAttemptId = attempt.attemptId;
      }
    }

    try {
      // Giữ nguyên cổng local gốc 13369
      const response: any = await this.http.post('http://localhost:13369/signing', data).toPromise();
      if (response?.success) {
        if (useSigningAttempt) {
          this.clearSigningAttempt(runValue);
        }
        AppSubject.setReloadData('success');
        this.runResolve?.();
      } else if (response?.errorMessage) {
        this.showErrorDialog(response);
        AppSubject.setReloadData('error');
        this.runReject?.(response);
      } else {
        const error = { errorMessage: this.getErrorMessage(response) };
        this.showErrorDialog(error);
        AppSubject.setReloadData('error');
        this.runReject?.(error);
      }
    } catch (error) {
      this.handleSigningError(error, runValue, false, useSigningAttempt);
      this.errorSubject.next(error);
      this.runReject?.(error);
      throw error;
    }
  }

  private async handleUsbError(error: any, runValue: RunValue, useSigningAttempt: boolean): Promise<void> {
    await this.completeSigning(runValue, "N", useSigningAttempt);
    this.errorSubject.next(error);
    this.runReject?.(error);
  }

  private async completeSigning(runValue: RunValue, success: string, useSigningAttempt = true): Promise<void> {
    const url = `${window.location.origin}/api/rest/s1/iam-common/complete-signing`;
    const input: any = {
      success,
      idValue: runValue.idValue,
      signingType: runValue.actionType
    };
    if (useSigningAttempt) {
      const attempt = this.getSigningAttempt(runValue);
      if (attempt?.attemptId) {
        input.signingAttemptId = attempt.attemptId;
      }
    }
    try {
      const result: any = await this.http.post(url, input, this.getHttpOptions()).toPromise();
      if (result?.idValue) {
        if (useSigningAttempt) {
          this.clearSigningAttempt(runValue);
        }
        AppSubject.setReloadData(success === "Y" ? 'success' : 'error');
        this.displaySubject.next('closeForm3');
        if (success === "Y") this.runResolve?.();
        else this.runReject?.(result);
      }
    } catch (error) {
      this.runReject?.(error);
    }
  }

  private async handleRemoteSigningFlow(signingType: string): Promise<void> {
    const runValue = this.getCurrentRunValue();
    const recordMethod = this.getRecordSigningMethod(signingType);
    if (this.isRecordSigning(runValue) && recordMethod) {
      if (!recordMethod.certList || !recordMethod.certList.length) {
        this.showErrorDialog({ errorMessage: recordMethod.unavailableReason || 'Chưa có chứng thư số hợp lệ cho hình thức ký này' });
        this.runReject?.(recordMethod);
        return;
      }
      UserService.set(UserService.KEY.REMOTE_SIGNING, JSON.stringify(recordMethod.certList));
      this.displaySubject.next('form2');
      return;
    }

    const url = `${window.location.origin}/api/rest/s1/iam-common/remote-signing?partyClassificationId=${signingType}`;
    try {
      const result: any = await this.http.get(url, this.getHttpOptions()).toPromise();
      if (result.listData) {
        UserService.set(UserService.KEY.REMOTE_SIGNING, JSON.stringify(result.listData));
        this.displaySubject.next('form2');
      }
    } catch (error) {
      this.showErrorDialog(error);
      this.runReject?.(error);
    }
  }

  private async handleAgreementPdfUsbSigning(runValue: RunValue): Promise<void> {
    try {
      await this.agreementSigningHandler.signAgreement({
        referenceCode: runValue.referenceCode || '',
        signedByRole: runValue.signedByRole || 'SELLER',
        signedByPartyId: runValue.signedByPartyId,
        onSuccess: () => {
          this.runResolve?.();
        },
        onError: (error: any) => {
          if (error?.isPluginError) {
            this.errorSubject.next({ status: 0 });
          } else {
            this.showErrorDialog({ errorMessage: error?.message || 'Không thể ký biên bản' });
          }
          AppSubject.setReloadData('error');
          this.runReject?.(error);
        }
      });
    } catch (error) {
      this.runReject?.(error);
    } finally {
      UserService.set(UserService.KEY.SIGNING, '');
    }
  }

  private getCurrentRunValue(): RunValue | undefined {
    try {
      const raw = UserService.get(UserService.KEY.RUN_VALUE);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  }

  private getRecordSigningMethod(signingType: string): any {
    try {
      const raw = UserService.get('RECORD_SIGNING_TYPES');
      const methods = raw ? JSON.parse(raw) : [];
      return methods.find((item: any) => item.partyClassificationId === signingType);
    } catch {
      return undefined;
    }
  }

  private processFinalResult(result: any, useSigningAttempt = true): void {
    if (result?.idValue) {
      if (useSigningAttempt) {
        this.clearSigningAttempt();
      }
      AppSubject.setReloadData('success');
      this.displaySubject.next('closeForm3');
      this.runResolve?.();
    } else if (result?.errorMessage) {
      this.showErrorDialog(result);
      this.handleFinalError();
      this.runReject?.(result);
    }
  }

  private handleFinalError(): void {
    AppSubject.setReloadData('error');
    this.displaySubject.next('closeForm3');
  }

  private showErrorDialog(error: any): void {
    if (!error) return;
    this.confirmService.setConfirmDialog({
      message: this.getErrorMessage(error),
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
  }

  private getHttpOptions() {
    return {
      headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') }
    };
  }

  private getErrorMessage(error: any): string {
    const body = error?.error || error;
    const nested = body?.data || body?.result || body?.response;
    const raw = body?.errorMessage || body?.errors || body?.message || body?.statusText
      || nested?.errorMessage || nested?.errors || nested?.message || nested?.statusText;
    if (Array.isArray(raw)) {
      return raw.join('\n');
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw;
    }
    if (error?.status === 0 || error?.name === 'TimeoutError') {
      return 'Không kết nối được công cụ ký điện tử. Vui lòng kiểm tra lại.';
    }
    return 'Không thể ký điện tử. Vui lòng thử lại hoặc liên hệ quản trị hệ thống.';
  }

  remoteSigning(data: any): Observable<any> {
    const runValue = this.getCurrentRunValue();
    if (this.isPendingBatchSigning(runValue)) {
      return this.remoteBatchSigning(data, runValue as RunValue);
    }

    const isIntrustPdfRecord = this.isPdfRecordSigning(runValue) && data?.partyClassificationId === 'IntrustRemoteSigning';
    const url = isIntrustPdfRecord
      ? `/api/rest/s1/iam-intrust/agreement-content-signing`
      : `/api/rest/s1/iam-common/remote-signing`;
    const {signingAttemptId: _signingAttemptId, ...remoteSigningData} = data || {};
    const payload = isIntrustPdfRecord
      ? {
          referenceCode: runValue?.referenceCode,
          agreementContentId: runValue?.agreementContentId,
          partyOtherIndentId: data.partyOtherIndentId,
          pinCode: data.pinCode
        }
      : remoteSigningData;
    const obs = this.http.post(url, payload, this.getHttpOptions());
    return new Observable(subscriber => {
      obs.subscribe({
        next: (val) => {
          this.runResolve?.();
          subscriber.next(val);
        },
        error: (err) => {
          this.handleSigningError(err, runValue, false, false);
          this.runReject?.(err);
          subscriber.error(err);
        },
        complete: () => subscriber.complete()
      });
    });
  }

  promptResumeBatchSigningAttempt(): void {
    if (this.batchResumePrompted || !this.canShowSigningDialog()) {
      return;
    }

    const attempt = this.findLatestBatchSigningAttempt();
    if (!attempt) {
      return;
    }

    this.batchResumePrompted = true;
    this.confirmService.setConfirmDialog({
      message: 'Đang thực hiện ký hóa đơn hàng loạt, bạn có muốn hoàn thành quy trình ký này không?',
      header: 'Ký hóa đơn hàng loạt',
      icon: 'pi pi-question-circle',
      extraButtons: [
        {
          label: 'Xem',
          icon: 'pi pi-list',
          styleClass: 'p-button-text',
          command: () => this.viewBatchSigningAttempt(attempt)
        }
      ],
      accept: () => this.resumeBatchSigningAttempt(attempt),
      reject: () => this.rejectBatchSigningAttempt(attempt)
    });
  }

  closeResumeBatchSigningPrompt(): void {
    this.batchResumePrompted = false;
    this.confirmService.closeConfirmDialog();
  }

  canShowSigningDialog(): boolean {
    const user = UserService.getUser() || {};
    return !!AppService.get(AppService.KEY.MOQUI_SESSION_TOKEN)
      && !!(user.partyId || user.userId || user.userLoginId)
      && !window.location.pathname.startsWith('/login');
  }

  private isPendingBatchSigning(runValue?: RunValue): boolean {
    return !!runValue?.idValues?.length
      && !runValue?.idValue
      && ['invoiceBatchSigning', 'posBatchSigning'].includes(runValue.actionType);
  }

  private remoteBatchSigning(data: any, runValue: RunValue): Observable<any> {
    return new Observable(subscriber => {
      if (this.isPosRemoteSigningWithoutUserToken(data, runValue)) {
        const {signingAttemptId: _signingAttemptId, ...remoteSigningData} = data || {};
        const payload = Object.assign({}, remoteSigningData, {
          idValues: runValue.idValues,
          signingType: runValue.actionType
        });

        this.http.post(`/api/rest/s1/iam-common/remote-signing`, payload, this.getHttpOptions()).subscribe({
          next: (val) => {
            this.runResolve?.();
            subscriber.next(val);
          },
          error: (err) => {
            this.runReject?.(err);
            subscriber.error(err);
          },
          complete: () => subscriber.complete()
        });
        return;
      }

      const prepare$ = runValue.actionType === 'posBatchSigning'
        ? this.invoiceService.POSSigning(runValue.idValues)
        : this.invoiceService.putToWorkEffort(runValue.idValues);

      prepare$.subscribe({
        next: (result: any) => {
          const preparedRunValue = {
            ...runValue,
            idValue: result?.workEffortId,
            signingData: result
          };
          if (!preparedRunValue.idValue) {
            subscriber.error(new Error('Không tạo được phiên ký hàng loạt'));
            return;
          }

          UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(preparedRunValue));
          const payload = Object.assign({}, data, {
            idValue: preparedRunValue.idValue,
            idValues: preparedRunValue.idValues,
            signingType: preparedRunValue.actionType
          });

          this.http.post(`/api/rest/s1/iam-common/remote-signing`, payload, this.getHttpOptions()).subscribe({
            next: (val) => {
              this.runResolve?.();
              subscriber.next(val);
            },
            error: (err) => {
              this.handleSigningError(err, preparedRunValue, false, false);
              this.runReject?.(err);
              subscriber.error(err);
            },
            complete: () => subscriber.complete()
          });
        },
        error: (err) => {
          this.runReject?.(err);
          subscriber.error(err);
        }
      });
    });
  }

  private isPosRemoteSigningWithoutUserToken(data: any, runValue?: RunValue): boolean {
    return runValue?.actionType === 'posBatchSigning'
      && data?.partyClassificationId
      && !['UsbTokenSigning', 'newCert'].includes(data.partyClassificationId);
  }

  private prepareSigningAttempt(runValue?: RunValue): SigningAttempt | undefined {
    if (!this.shouldUseSigningAttempt(runValue)) {
      return undefined;
    }

    const key = this.getSigningAttemptKey(runValue);
    if (!key) {
      return undefined;
    }

    const invoiceId = this.getSigningAttemptInvoiceId(runValue);
    const attemptIdentity = invoiceId || runValue?.idValue || '';
    const stored = localStorage.getItem(key);
    if (stored) {
      const existingAttempt = JSON.parse(stored) as SigningAttempt;
      const existingIdentity = existingAttempt.invoiceId || existingAttempt.idValue;
      if (existingIdentity === attemptIdentity) {
        return existingAttempt;
      }
    }

    const issueStatusId = runValue?.issueStatusId || (runValue?.actionType === 'toKhai100' ? 'EiurNotSent' : '');
    const canStartNewAttempt = this.canStartNewAttempt(runValue?.actionType || '', issueStatusId);

    if (!canStartNewAttempt) {
      throw new Error(
        issueStatusId === 'InvIssuing'
          ? 'Hóa đơn đang được phát hành tại một phiên làm việc khác.'
          : 'Trạng thái hóa đơn không cho phép thực hiện ký.'
      );
    }

    const userId = this.getSigningUserId();
    const serverAttemptId = runValue?.signingData?.signingAttemptId;
    const attempt: SigningAttempt = {
      attemptId: serverAttemptId || this.createUuid(),
      userId,
      signingType: runValue?.actionType || '',
      idValue: runValue?.idValue || '',
      ...(invoiceId ? {invoiceId} : {}),
      createdAt: Date.now()
    };

    localStorage.setItem(key, JSON.stringify(attempt));
    return attempt;
  }

  private getSigningAttempt(runValue?: RunValue): SigningAttempt | undefined {
    const key = this.getSigningAttemptKey(runValue);
    if (!key) {
      return undefined;
    }
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : undefined;
  }

  private findLatestBatchSigningAttempt(): SigningAttempt | undefined {
    const prefix = `signing-attempt:${this.getSigningUserId()}:`;
    const attempts: SigningAttempt[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index) || '';
      if (!key.startsWith(prefix)) {
        continue;
      }

      const parts = key.split(':');
      const signingType = parts[2];
      if (!['invoiceBatchSigning', 'posBatchSigning'].includes(signingType)) {
        continue;
      }

      const stored = localStorage.getItem(key);
      if (!stored) {
        continue;
      }

      try {
        const attempt = JSON.parse(stored) as SigningAttempt;
        if (attempt?.attemptId && attempt?.idValue && attempt?.signingType) {
          attempts.push(attempt);
        }
      } catch {
      }
    }

    return attempts.sort((left, right) => (right.createdAt || 0) - (left.createdAt || 0))[0];
  }

  private resumeBatchSigningAttempt(attempt: SigningAttempt): void {
    const runValue = this.getBatchSigningRunValue(attempt);

    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    UserService.set(UserService.KEY.SIGNING, 'UsbTokenSigning');
    this.run(runValue).catch(() => {
      UserService.set(UserService.KEY.SIGNING, '');
    });
  }

  private rejectBatchSigningAttempt(attempt: SigningAttempt): void {
    const runValue = this.getBatchSigningRunValue(attempt);

    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    this.completeSigning(runValue, 'N').finally(() => {
      this.batchResumePrompted = false;
    });
  }

  private viewBatchSigningAttempt(attempt: SigningAttempt): void {
    this.invoiceService.getBatchSigningView(attempt.idValue).subscribe({
      next: (response: any) => {
        this.batchSigningViewSubject.next({
          workEffortId: attempt.idValue,
          items: this.extractListData(response)
        });
      },
      error: (error: any) => this.showErrorDialog(error)
    });
  }

  private extractListData(response: any): Array<any> {
    const body = response || {};
    const candidates = [
      body.listData,
      body.data,
      body.items,
      body.invoices,
      body.result
    ];
    const list = candidates.find((value: any) => Array.isArray(value));
    if (list) {
      return list;
    }
    return Array.isArray(response) ? response : [];
  }

  private getBatchSigningRunValue(attempt: SigningAttempt): RunValue {
    return {
      idValue: attempt.idValue,
      actionType: attempt.signingType,
      issueStatusId: 'InvNotIssued'
    };
  }

  private clearSigningAttempt(runValue?: RunValue): void {
    const key = this.getSigningAttemptKey(runValue || this.getCurrentRunValue());
    if (key) {
      localStorage.removeItem(key);
    }
  }

  private getSigningAttemptKey(runValue?: RunValue): string {
    if (!this.shouldUseSigningAttempt(runValue) || !runValue?.idValue) {
      return '';
    }
    return `signing-attempt:${this.getSigningUserId()}:${runValue.actionType}`;
  }

  private getSigningAttemptInvoiceId(runValue?: RunValue): string {
    if (!runValue?.invoiceId && !this.isInvoiceSigningAction(runValue)) {
      return '';
    }
    return `${runValue?.invoiceId || runValue?.idValue || ''}`;
  }

  private isInvoiceSigningAction(runValue?: RunValue): boolean {
    return runValue?.actionType === 'invoiceBatchSigning'
      || runValue?.actionType === 'posBatchSigning'
      || runValue?.actionType?.indexOf('Invoice') === 0;
  }

  private shouldUseSigningAttempt(runValue?: RunValue): boolean {
    if (!runValue?.actionType || !runValue?.idValue) {
      return false;
    }
    return runValue.actionType === 'toKhai100'
      || runValue.actionType === 'invoiceBatchSigning'
      || runValue.actionType === 'posBatchSigning'
      || runValue.actionType.indexOf('Invoice') === 0;
  }

  private canStartNewAttempt(signingType: string, issueStatusId: string): boolean {
    if (signingType === 'toKhai100') {
      return !issueStatusId || ['EiurNotSent', 'EiurSellerSigningError', 'EiurDataNotValid'].includes(issueStatusId);
    }
    if (!issueStatusId) {
      return true;
    }
    return ['InvNotIssued', 'InvIssuedError'].includes(issueStatusId);
  }

  private getSigningUserId(): string {
    const user = UserService.getUser() || {};
    return user.userId || user.userLoginId || user.partyId || user.partyTaxId || 'anonymous';
  }

  private createUuid(): string {
    const cryptoRef = window.crypto as any;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === 'x' ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }

  private handleSigningError(error: any, runValue?: RunValue, showDialog = true, useSigningAttempt = true): void {
    const code = this.getBusinessErrorCode(error);
    if (useSigningAttempt && code === 'SIGNING_ATTEMPT_NOT_OWNER') {
      this.clearSigningAttempt(runValue);
      if (showDialog) {
        this.showErrorDialog({ errorMessage: 'Hóa đơn đang được phát hành tại một phiên làm việc khác.' });
      }
      return;
    }
    if (showDialog) {
      this.showErrorDialog(error?.error || error);
    }
  }

  private getBusinessErrorCode(error: any): string {
    const body = error?.error || error;
    return body?.code || body?.errorCode || body?.error || '';
  }

  private isSigningAttemptGuardError(error: any): boolean {
    return error instanceof Error && [
      'Hóa đơn đang được phát hành tại một phiên làm việc khác.',
      'Trạng thái hóa đơn không cho phép thực hiện ký.'
    ].includes(error.message);
  }
}
